import React from 'react';
import { cn } from '@/lib/utils';
import {
  OffsetComparisonTimeline,
  type OffsetBracket,
  type OffsetLadderEntry,
} from './OffsetComparisonTimeline';
import { EquationValueBadge } from './EquationValueBadge';
import { NumberStepper } from '@/components/ui/number-stepper';
import {
  CONTROL_ROW_BODY_CLASS,
  CONTROL_ROW_LABEL_CLASS,
} from '@/components/config_forms/shared/ControlRow';

export interface CountModeOffsetGraphicProps {
  /** Brackets keyed by ladder tier — same shape OffsetComparisonTimeline expects. */
  brackets: OffsetBracket[];
  /** Full effective ladder (base ST + overtime tiers, plus any Extended Overtime rungs). */
  effectiveLadder: OffsetLadderEntry[];
  /** Current configured per-miss bump (1..maxOffset). */
  overtimeOffset: number;
  /** Current configured required breaks (live from safeConfig.requiredBreaks). May be null/0/NaN. */
  requiredBreaks: number | null;
  /** Maximum value the editor will accept. */
  maxOffset: number;
  /** Disabled when the ladder cannot support an offset (≤1 base tier). */
  offsetDisabled: boolean;
  /** Optional write-back. When undefined the editor renders read-only. */
  onOvertimeOffsetChange?: (value: number) => void;
  /** Extra controls rendered below the editor row (e.g. Extended Overtime section). */
  additionalControls?: React.ReactNode;
}

const FALLBACK_CLASS =
  'rounded border border-border/50 bg-muted/25 px-3 py-2 text-center text-[10px] sm:text-[12px] text-muted-foreground';

function FallbackMessage({ children }: { children: React.ReactNode }) {
  return (
    <div className={FALLBACK_CLASS} data-testid="count-mode-offset-fallback">
      {children}
    </div>
  );
}

function deriveMissedBreaks(requiredBreaks: number | null): number | null {
  if (
    requiredBreaks == null ||
    !Number.isFinite(requiredBreaks) ||
    requiredBreaks < 1
  ) {
    return null;
  }
  const required = Math.floor(requiredBreaks);
  const takenCount = required <= 1 ? 0 : 1;
  return required - takenCount;
}

/**
 * Normalize a possibly-unsafe `overtimeOffset` to a finite integer ≥ 1.
 *
 * The component's prop type declares `number`, but real callers can pass
 * `NaN`, `0`, or negative values during partial edits or stale-data flows.
 * Letting those through would surface as `+0 tiers` / `+NaN tiers` in the
 * equation header and silently floor to `1` inside `OffsetComparisonTimeline`
 * (which clamps `offsetIndex` via `Math.max(1, Math.floor(offsetIndex))` at
 * `OffsetComparisonTimeline.tsx:70`), making the equation and the bar
 * disagree. Defense-in-depth: normalize before rendering.
 *
 * `maxOffset` is also normalized — a non-finite or non-positive `maxOffset`
 * (defensive) treats the upper bound as `1`.
 */
function normalizeOvertimeOffset(
  raw: number | null | undefined,
  maxOffset: number,
): number {
  const safeMax =
    Number.isFinite(maxOffset) && maxOffset >= 1 ? Math.floor(maxOffset) : 1;
  if (raw == null || !Number.isFinite(raw)) return 1;
  const parsed = Math.floor(raw);
  if (parsed < 1) return 1;
  return Math.min(parsed, safeMax);
}

/**
 * Mirrors the internal validity guards of `OffsetComparisonTimeline`
 * (`OffsetComparisonTimeline.tsx:66-87`) so this wrapper can route to the
 * "Bump preview unavailable" fallback instead of letting the timeline
 * silently `return null` and leave a blank band between the equation header
 * and the editor row.
 *
 * The timeline rejects the input if (a) fewer than 2 brackets have a finite
 * positive `flex` and finite non-negative `naturalMultiplier`, (b) any ladder
 * entry has a non-finite or negative multiplier, or (c) the ladder is
 * shorter than the brackets array (shape mismatch — would index out of
 * range). All three must hold here too.
 */
function canTimelineRender(
  brackets: OffsetBracket[],
  effectiveLadder: OffsetLadderEntry[],
): boolean {
  const validBrackets = brackets.filter(
    (b) =>
      Number.isFinite(b?.flex) &&
      b.flex > 0 &&
      Number.isFinite(b?.naturalMultiplier) &&
      b.naturalMultiplier >= 0,
  );
  if (validBrackets.length < 2) return false;
  const ladderAllValid = effectiveLadder.every(
    (e) => Number.isFinite(e?.multiplier) && e.multiplier >= 0,
  );
  if (!ladderAllValid) return false;
  if (effectiveLadder.length < brackets.length) return false;
  return true;
}

// Operator className lifted verbatim from CountModeEquationLine in
// CreditExtraTimeTimeline.tsx:438-440. Reused here so the × and = operators
// share visual weight with the existing count-mode equation graphics.
const OPERATOR_CLASS =
  'mt-4 h-8 flex items-center justify-center font-mono text-[18px] leading-none text-foreground/85 md:justify-self-center';

interface EquationHeaderProps {
  missedBreaks: number | null;
  overtimeOffset: number;
  effectiveLadderLength: number;
}

function EquationHeader({
  missedBreaks,
  overtimeOffset,
  effectiveLadderLength,
}: EquationHeaderProps) {
  const breaksMissedDisplay = missedBreaks == null ? '—' : `${missedBreaks}`;
  const offsetDisplay = `+${overtimeOffset} tier${overtimeOffset === 1 ? '' : 's'}`;

  let totalBumpDisplay: string;
  let cappedAnnotation: string | null = null;
  if (missedBreaks == null) {
    totalBumpDisplay = '—';
  } else {
    const rawProduct = overtimeOffset * missedBreaks;
    totalBumpDisplay = `+${rawProduct}`;
    const ladderCap = Math.max(0, effectiveLadderLength - 1);
    const HARD_CAP = 50;
    if (rawProduct > Math.min(ladderCap, HARD_CAP)) {
      cappedAnnotation = '(capped)';
    }
  }

  return (
    <div
      data-testid="count-mode-offset-equation"
      className="w-full rounded-[0.25rem] border border-border/30 bg-surface-2/30 px-3 py-3"
    >
      <div className="w-full flex flex-wrap items-start justify-center gap-x-1 gap-y-2 md:grid md:grid-cols-[max-content_minmax(16px,1fr)_max-content_minmax(16px,1fr)_max-content] md:justify-items-center md:items-start md:gap-x-2 md:gap-y-0">
        <EquationValueBadge
          className="min-w-[72px] md:min-w-0 md:w-full"
          label="Breaks Missed"
          value={breaksMissedDisplay}
        />
        <div className={OPERATOR_CLASS} aria-hidden="true">×</div>
        <EquationValueBadge
          className="min-w-[88px] md:min-w-0 md:w-full"
          label="Overtime Offset"
          value={offsetDisplay}
        />
        <div className={OPERATOR_CLASS} aria-hidden="true">=</div>
        <div
          data-testid="count-mode-offset-total-bump"
          className="min-w-[88px] md:min-w-0 md:w-full text-center"
        >
          <div className="h-[12px] font-label text-[10px] sm:text-[12px] leading-none uppercase tracking-[1.1px] text-muted-foreground mb-1 flex items-center justify-center">
            Total Bump
          </div>
          <div className="w-full h-8 flex items-center justify-center font-mono text-[15px] font-semibold text-primary-text gap-1">
            <span>{totalBumpDisplay}</span>
            {cappedAnnotation && (
              <span className="font-label text-[10px] sm:text-[11px] uppercase tracking-[1.1px] text-muted-foreground">
                {cappedAnnotation}
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="mt-2 text-center text-[10px] sm:text-[12px] leading-relaxed text-foreground">
        Each missed break bumps your shift up the rate ladder, capped at the top tier.
      </div>
    </div>
  );
}

interface EditorRowProps {
  value: number;
  maxOffset: number;
  disabled: boolean;
  onChange?: (value: number) => void;
}

function OvertimeOffsetEditorRow({ value, maxOffset, disabled, onChange }: EditorRowProps) {
  const isInteractive = !disabled && typeof onChange === 'function';
  return (
    <div
      data-testid="count-mode-offset-editor-row"
      className="px-3 py-2 grid w-full grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-1 items-center sm:flex sm:items-center sm:justify-between sm:gap-4"
    >
      <div className="min-w-0">
        <span className={CONTROL_ROW_LABEL_CLASS}>Overtime Offset</span>
        <p className={cn(CONTROL_ROW_BODY_CLASS, 'hidden sm:block')}>
          Number of overtime ladder steps to shift upward when a meal break is missed.
        </p>
      </div>
      <div className="flex flex-col items-end">
        <NumberStepper
          ariaLabel="Meal break overtime offset"
          label="Meal break overtime offset"
          value={value}
          min={1}
          max={maxOffset}
          disabled={!isInteractive}
          onChange={(next) => onChange?.(next)}
        />
      </div>
      <p className={cn(CONTROL_ROW_BODY_CLASS, 'col-span-2 sm:hidden')}>
        Number of overtime ladder steps to shift upward when a meal break is missed.
      </p>
    </div>
  );
}

export const CountModeOffsetGraphic: React.FC<CountModeOffsetGraphicProps> = ({
  brackets,
  effectiveLadder,
  overtimeOffset,
  requiredBreaks,
  maxOffset,
  offsetDisabled,
  onOvertimeOffsetChange,
  additionalControls,
}) => {
  // Normalize the user-facing knob first so every downstream display + computation
  // shares the same finite integer ≥ 1.
  const safeOvertimeOffset = normalizeOvertimeOffset(overtimeOffset, maxOffset);
  const missedBreaks = deriveMissedBreaks(requiredBreaks);

  // Fallback selection — order matters: the most specific message wins.
  // 1. Ladder too short → "configure overtime multiplier"
  // 2. requiredBreaks invalid → "set required breaks"
  // 3. Brackets unrenderable for any other reason → generic "preview unavailable"
  //
  // The third branch must ALSO mirror OffsetComparisonTimeline's internal
  // validity checks (OffsetComparisonTimeline.tsx:66-87). If those fail, the
  // timeline returns null and the user would see a blank band between the
  // equation header and the editor row. We pre-check them here so the generic
  // fallback renders instead.
  let fallback: React.ReactNode = null;
  let offsetIndex: number | null = null;
  if (effectiveLadder.length <= 1) {
    fallback = <FallbackMessage>Configure at least one overtime multiplier to preview the bump.</FallbackMessage>;
  } else if (missedBreaks == null) {
    fallback = <FallbackMessage>Set Required Breaks above to preview the bump.</FallbackMessage>;
  } else if (!canTimelineRender(brackets, effectiveLadder)) {
    fallback = <FallbackMessage>Bump preview unavailable for this configuration.</FallbackMessage>;
  } else {
    // All guards passed: compute the clamped offsetIndex the bar will consume.
    // Clamp to both ladder.length-1 (don't exceed the top natural tier) and
    // the OffsetComparisonTimeline's internal HARD_CAP=50 (OffsetComparisonTimeline.tsx:~70)
    // so the equation "(capped)" annotation and the bar's aria-label agree.
    const rawProduct = safeOvertimeOffset * missedBreaks;
    const HARD_CAP = 50;
    offsetIndex = Math.min(rawProduct, effectiveLadder.length - 1, HARD_CAP);
  }

  return (
    <div data-testid="count-mode-offset-graphic" className="flex flex-col gap-3">
      <EquationHeader
        missedBreaks={missedBreaks}
        overtimeOffset={safeOvertimeOffset}
        effectiveLadderLength={effectiveLadder.length}
      />
      {fallback ?? (
        offsetIndex != null && (
          <OffsetComparisonTimeline
            brackets={brackets}
            effectiveLadder={effectiveLadder}
            offsetIndex={offsetIndex}
            headingLabel={null}
          />
        )
      )}
      <OvertimeOffsetEditorRow
        value={safeOvertimeOffset}
        maxOffset={maxOffset}
        disabled={offsetDisabled}
        onChange={onOvertimeOffsetChange}
      />
      {additionalControls != null && (
        <div className="px-3 py-2">{additionalControls}</div>
      )}
    </div>
  );
};
